import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "flex flex-col w-full items-center space-y-6 justify-center" }
const _hoisted_2 = { class: "flex flex-col w-full max-w-xl" }
const _hoisted_3 = { class: "text-lg font-medium text-gray-900 text-left" }
const _hoisted_4 = { class: "p-6 space-y-6" }
const _hoisted_5 = { class: "flex flex-row" }

import { onBeforeMount, ref, watch } from 'vue';
import axios, { AxiosRequestConfig } from 'axios';
import { useI18n } from 'vue-i18n';
import { IErrors } from '@/lib';
import { BaseCard } from '@/lib/components/Card';
import UnitOfTimeInput from '@/lib/components/Input/UnitOfTimeInput.vue';
import { MedKitSelect, MedKitRadioGroup, SelectionOption } from '@bigpicturemedical/medkit';
import { FilterTag, IbdProgramPayload, TagGroupName } from '@/models';
import WorklistService from '@/services/api/worklist.service';
import { usePathwayName } from '@/composables/pathways';
import { YES_NO_RADIO, PREFERED_METHOD_FOR_TEST } from '@/constants/selectOptions';


export default /*#__PURE__*/_defineComponent({
  __name: 'IbdSetup',
  props: {
    program: {},
    pathway: {},
    errors: {}
  },
  emits: ["update-program"],
  setup(__props: any, { emit: __emit }) {

const inputErrors = ref<IErrors>({});
const fetchErrors = ref<IErrors | null>(null);
const hospitalTeams = ref<FilterTag[]>([]);
const hospitalTeamsOptions = ref<SelectionOption[]>([]);
const request = ref(null);
const worklistService = new WorklistService();
const { t } = useI18n();
const props = __props;
const emit = __emit;

watch(() => props.errors, (newVal) => {
  inputErrors.value = newVal;
});

onBeforeMount(() => {
  fetchHospitalTeams();
});

const fetchHospitalTeams = async () => {
  try {
    request.value = axios.CancelToken.source();
    const requestConfig: AxiosRequestConfig = {
      params: {
        group: TagGroupName.HospitalTeam
      },
      cancelToken: request.value.token
    };
    hospitalTeams.value = await worklistService.getWorklistTags(props.pathway, requestConfig);
    hospitalTeamsOptions.value = hospitalTeams.value.map((team) => ({ value: team.id, label: team.name }));
  } catch (e) {
    fetchErrors.value = {};
    if (!axios.isAxiosError(e)) {
      throw e;
    }
    if (e.response.status === 422) {
      fetchErrors.value = e.response.data?.errors;
    }
  }
};

const updateProgram = (updated: IbdProgramPayload): void => {
  const updatedProgram = {
    ...props.program,
    ...updated
  };

  exposed.triggerUpdateProgram(updatedProgram);

  Object.keys(updated).forEach((updatedField) => {
    if (inputErrors.value?.[`${updatedField}`]){
      delete inputErrors.value[`${updatedField}`];
    }
  });
};

const triggerUpdateProgram = (updated: IbdProgramPayload): void => {
  emit('update-program', updated);
};

const exposed = {
  triggerUpdateProgram,
  updateProgram
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("h1", _hoisted_3, _toDisplayString(_unref(t)('custom.uhb.pathways.add-patient-to', { pathwayName: _unref(usePathwayName)(props.pathway) })), 1)
    ]),
    _createVNode(_unref(BaseCard), {
      class: "flex flex-col w-full max-w-xl",
      padding: "0"
    }, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_4, [
          _createElementVNode("div", _hoisted_5, [
            _createVNode(_unref(MedKitSelect), {
              error: inputErrors.value?.hospital_team,
              label: _unref(t)('custom.uhb.copd.hospital-team'),
              options: hospitalTeamsOptions.value,
              placeholder: _unref(t)('custom.uhb.copd.select-entity',[(_unref(t)('custom.uhb.copd.hospital-team')).toLowerCase()]),
              required: "",
              "model-value": (_ctx.program as IbdProgramPayload).hospital_team,
              "data-dusk": "ibd-hospital-team-dropdown",
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => (updateProgram({ hospital_team: $event })))
            }, null, 8, ["error", "label", "options", "placeholder", "model-value"])
          ]),
          _createElementVNode("div", null, [
            _createVNode(UnitOfTimeInput, {
              error: inputErrors.value?.submission_date?.[0],
              required: "",
              label: _unref(t)('custom.uhb.ibd.patient-first-submission-due-in'),
              "unit-placeholder": _unref(t)('custom.uhb.review.unit-of-time'),
              "data-dusk": "date-section",
              onChange: _cache[1] || (_cache[1] = ($event: any) => (updateProgram({ submission_date: $event.date as string })))
            }, null, 8, ["error", "label", "unit-placeholder"])
          ]),
          _createElementVNode("div", null, [
            _createVNode(_unref(MedKitRadioGroup), {
              error: inputErrors.value?.test_type,
              label: _unref(t)('custom.uhb.ibd.patients-preferred-test-method'),
              options: _unref(PREFERED_METHOD_FOR_TEST),
              name: "patient-preferred-method-for-foecal-calprotectin-test",
              required: "",
              "data-dusk": "patients-preferred-test-method",
              "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => (updateProgram({ test_type: $event })))
            }, null, 8, ["error", "label", "options"])
          ]),
          _createElementVNode("div", null, [
            _createVNode(_unref(MedKitRadioGroup), {
              error: inputErrors.value?.test_needed_for_next_review,
              label: _unref(t)('custom.uhb.ibd.test-needed-for-next-review'),
              options: _unref(YES_NO_RADIO),
              name: "faecal-calprotectin-test-needed-for-next-review",
              required: "",
              "data-dusk": "test-needed-for-next-review",
              "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => (updateProgram({ test_needed_for_next_review: $event })))
            }, null, 8, ["error", "label", "options"])
          ])
        ])
      ]),
      _: 1
    })
  ]))
}
}

})